// src/contexts/AuthContext.tsx

import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  User,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc, addDoc, collection, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { clearUrlCache } from '../services/signedUrlService';

// Roles permitidos no sistema - SEMPRE em UPPERCASE
export const SYSTEM_ROLES = {
  ADMIN: 'ADMIN',
  REVISOR_CLINICO: 'REVISOR_CLINICO',
  LEITOR: 'LEITOR'
} as const;

export type SystemRole = typeof SYSTEM_ROLES[keyof typeof SYSTEM_ROLES];

// Roles que podem fazer rotulagem
export const LABELING_ROLES: SystemRole[] = [SYSTEM_ROLES.ADMIN, SYSTEM_ROLES.REVISOR_CLINICO];

// Interface do perfil do usuário
export interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  role: SystemRole;
  photoURL?: string;
  createdAt?: Date;
}

// Interface do contexto
interface AuthContextType {
  currentUser: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  logout: () => Promise<void>;
  canLabel: () => boolean;
}

// Criar contexto
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Hook para usar o contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

// Provider do contexto
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * Carrega o perfil do usuário do Firestore
   */
  const loadUserProfile = async (user: User): Promise<UserProfile | null> => {
    try {
      // Validar email verificado
      if (!user.emailVerified) {
        console.error('Email não verificado');
        await signOut(auth);
        throw new Error('Email não verificado. Verifique seu email antes de acessar o sistema.');
      }

      // Validar domínio permitido
      const email = user.email || '';
      if (!email.endsWith('@shelistens.com.br')) {
        console.error('Domínio não permitido:', email);
        await signOut(auth);
        throw new Error('Acesso restrito a contas @shelistens.com.br');
      }

      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (userDoc.exists()) {
        const data = userDoc.data();
        const profile = {
          uid: user.uid,
          email: user.email || '',
          displayName: data.displayName || user.displayName || 'Usuário',
          role: data.role || 'user',
          photoURL: user.photoURL || data.photoURL,
          createdAt: data.createdAt?.toDate()
        };

        // Registrar login bem-sucedido
        try {
          await addDoc(collection(db, 'audit_logs'), {
            actor_uid: user.uid,
            actor_email: user.email,
            action: 'LOGIN',
            result: 'GRANT',
            email_domain: email.split('@')[1],
            timestamp: new Date(),
            details: {
              role: profile.role,
              email_verified: user.emailVerified
            }
          });
        } catch (auditError) {
          console.warn('Erro ao registrar auditoria de login:', auditError);
        }

        return profile;
      } else {
        // Se o documento não existe, cria um perfil básico no Firestore
        console.warn('Perfil do usuário não encontrado no Firestore. Criando novo perfil...');
        
        const newProfile = {
          uid: user.uid,
          email: user.email || '',
          displayName: user.displayName || 'Usuário',
          role: SYSTEM_ROLES.LEITOR, // Role padrão
          photoURL: user.photoURL || '',
          createdAt: serverTimestamp(),
          emailVerified: user.emailVerified
        };

        // Criar documento no Firestore
        await setDoc(doc(db, 'users', user.uid), newProfile);

        const profile: UserProfile = {
          uid: user.uid,
          email: user.email || '',
          displayName: user.displayName || 'Usuário',
          role: SYSTEM_ROLES.LEITOR,
          photoURL: user.photoURL || undefined,
          createdAt: new Date()
        };

        // Registrar criação de usuário
        try {
          await addDoc(collection(db, 'audit_logs'), {
            actor_uid: user.uid,
            actor_email: user.email,
            action: 'USER_CREATED',
            result: 'GRANT',
            email_domain: email.split('@')[1],
            timestamp: new Date(),
            details: {
              role: profile.role,
              email_verified: user.emailVerified
            }
          });
        } catch (auditError) {
          console.warn('Erro ao registrar auditoria de criação:', auditError);
        }

        return profile;
      }
    } catch (error) {
      console.error('Erro ao carregar perfil do usuário:', error);
      
      // Registrar tentativa de acesso negada
      try {
        await addDoc(collection(db, 'audit_logs'), {
          actor_uid: user.uid,
          actor_email: user.email,
          action: 'LOGIN',
          result: 'DENY',
          reason: error instanceof Error && error.message.includes('domínio') 
            ? 'domain_restricted' 
            : error instanceof Error && error.message.includes('verificado')
            ? 'email_unverified'
            : 'profile_load_error',
          email_domain: user.email?.split('@')[1] || 'unknown',
          timestamp: new Date(),
          details: {
            error_message: error instanceof Error ? error.message : 'Unknown error',
            email_verified: user.emailVerified
          }
        });
      } catch (auditError) {
        console.warn('Erro ao registrar auditoria de negação:', auditError);
      }
      
      throw error;
    }
  };

  /**
   * Logout do usuário
   */
  const logout = async () => {
    try {
      // Limpar cache de URLs antes do logout para segurança
      clearUrlCache();
      await signOut(auth);
      setUserProfile(null);
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
      throw error;
    }
  };

  /**
   * Verifica se o usuário pode rotular áudios
   * Validação com roles normalizadas e type-safe
   */
  const canLabel = (): boolean => {
    if (!userProfile) return false;

    // Normalizar role para UPPERCASE para comparação case-insensitive
    const normalizedRole = userProfile.role.toUpperCase() as SystemRole;

    // Verificar se a role está na lista de roles permitidas
    return LABELING_ROLES.includes(normalizedRole);
  };

  /**
   * Listener de mudanças no estado de autenticação
   */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        // Usuário logado - carregar perfil
        const profile = await loadUserProfile(user);
        setUserProfile(profile);
      } else {
        // Usuário deslogado
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    // Cleanup
    return unsubscribe;
  }, []);

  const value: AuthContextType = {
    currentUser,
    userProfile,
    loading,
    logout,
    canLabel
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
